// Walking OVER the rice rows. Each paddy's crop rows are raised mounds; anything that walks across one is
// lifted onto the mound surface (instead of sinking into it and vanishing). Characters call groundLift(x, z)
// each frame and add it to their ground height.
//
// A row is the Capital "Env_CropRow" piece: 1.46 m wide, 7.94 m long, mound ≈ 0.45 m high (plants rise above that).

const MOUND_M = 0.45   // mound height in source metres
const HALF_W_M = 0.73  // half width
const HALF_L_M = 3.97  // half length
const TAPER_M = 1.3    // the ends of the row slope down over this length
export const ROW_HALF_LEN_M = HALF_L_M

const paddies = new Set() // { wrap, rows: [{ lx, lz }] }  (lx/lz = row centre offset in the wrap's local metres)

export function registerPaddyRows(wrap, rows) { // rows: [{ lx, lz, sy? }] — sy = that row's own height scale (set by riceField as it grows); else the wrap's
  const p = { wrap, rows }
  paddies.add(p)
  return p
}
export function unregisterPaddyRows(wrap) {
  for (const p of paddies) if (p.wrap === wrap) paddies.delete(p)
}
export function clearPaddyRows() { paddies.clear() }

// Extra height (world units) above the normal ground at world (x, z): 0 off the rows, up to the mound top on one.
export function groundLift(x, z) {
  let best = 0
  for (const { wrap, rows } of paddies) {
    const k = wrap.scale.x                      // metres → world
    const dxw = x - wrap.position.x, dzw = z - wrap.position.z
    if (dxw * dxw + dzw * dzw > 36 * k * k) continue // farther than ~6 m from this paddy's centre
    const sy = wrap.scale.y                      // the rice grows/shrinks with the season; so does the mound
    for (const r of rows) {
      const ax = Math.abs(dxw / k - r.lx)
      const az = Math.abs(dzw / k - r.lz)
      if (ax >= HALF_W_M || az >= HALF_L_M) continue
      const across = 1 - (ax / HALF_W_M) ** 2                 // rounded cross-section
      const along = Math.min(1, (HALF_L_M - az) / TAPER_M)    // sloping ends
      const h = MOUND_M * across * along * (r.sy ?? sy)
      if (h > best) best = h
    }
  }
  return best
}

// The paddies as rows to run along, in world units: [{ id, cx, cz, rows: [{ x, z0, z1 }] }] (rows sorted by x; each row runs
// along z, `endMarginM` source metres short of its ends). Paddies still being built or already removed are left out.
export function getPaddyFields(endMarginM = 0.5) {
  const out = []
  for (const { wrap, rows } of paddies) {
    if (!wrap.parent) continue
    const k = wrap.userData.baseScale ?? wrap.scale.x // metres → world (the pop-in animation changes scale, not this)
    if (!(k > 0.01)) continue
    const half = Math.max(0.2, HALF_L_M - endMarginM) * k
    const rs = rows.map((r) => {
      const z = wrap.position.z + r.lz * k
      return { x: wrap.position.x + r.lx * k, z0: z - half, z1: z + half }
    }).sort((a, b) => a.x - b.x)
    out.push({ id: wrap.uuid, cx: wrap.position.x, cz: wrap.position.z, rows: rs })
  }
  return out
}
